import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentApi, matchApi, resultsApi, schoolApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import { clearSlipDraft, readSlipDraft } from '../../utils/slipDraft.js';
import { AggregateRing, Button, Card, Chips, Grid2, Muted, Spinner, Tag, useAsync } from '../../ui.jsx';

const statusTone = { yes: 'ok', fp: 'mid', reach: 'mid', long: 'no', no: 'no' };

export default function Match() {
  const navigate = useNavigate();
  const notice = useNotice();

  const reference = useAsync(() => schoolApi.reference(), []);
  const results = useAsync(() => resultsApi.get(), []);
  const match = useAsync(
    () => matchApi.summary({ status: 'All', school: 'All', limit: 200 }),
    [results.data]
  );

  const [filter, setFilter] = useState('All');
  const [school, setSchool] = useState('All');
  const [limit, setLimit] = useState(20);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);

  // A slip read from the profile page is waiting here as a draft. It is only a
  // starting point: the student still reviews and saves it on this page.
  const [slipSeed] = useState(readSlipDraft);
  const [slipNotice, setSlipNotice] = useState(Boolean(slipSeed?.result));
  const [draft, setDraft] = useState(() =>
    slipSeed?.result
      ? {
          eng: slipSeed.result.eng || 0,
          math: slipSeed.result.math || 0,
          sci: slipSeed.result.sci || 0,
          soc: slipSeed.result.soc || 0,
          el: slipSeed.result.el?.length
            ? slipSeed.result.el
            : [0, 1, 2, 3].map(() => ({ c: '', g: 0 }))
        }
      : null
  );

  useEffect(() => {
    if (slipSeed?.result) clearSlipDraft();
  }, [slipSeed]);

  const grades = reference.data?.grades || [];
  const subjects = reference.data?.subjects || {};
  const gradeValue = (code) => grades.findIndex((g) => g === code) + 1;

  const current = results.data?.result || null;
  const form = draft || {
    eng: current?.eng || 0,
    math: current?.math || 0,
    sci: current?.sci || 0,
    soc: current?.soc || 0,
    el: current?.el?.length
      ? current.el
      : [0, 1, 2, 3].map(() => ({ c: '', g: 0 }))
  };
  const set = (patch) => setDraft({ ...form, ...patch });
  const setCore = (key) => (e) => set({ [key]: Number(e.target.value) || 0 });
  const setElective = (index, patch) => {
    const el = form.el.map((item, i) => (i === index ? { ...item, ...patch } : item));
    set({ el });
  };

  if (reference.loading || results.loading) return <Spinner label="Loading your results" />;

  const aggregate = results.data?.aggregate ?? null;
  const programmes = match.data?.programmes || [];
  const counts = match.data?.counts || {};

  const save = async () => {
    setSaving(true);
    try {
      const data = await resultsApi.save(form);
      notice.success(`Results saved. Your aggregate is ${data.aggregate}.`);
      results.setData({ result: data.result, aggregate: data.aggregate, band: data.band });
      setDraft(null);
      setSlipNotice(false);
      match.reload();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    try {
      await resultsApi.clear();
      setDraft(null);
      results.reload();
      notice.success('Results cleared.');
    } catch (error) {
      notice.fromError(error);
    }
  };

  /**
   * The slip is uploaded as a document, then read by id. Whatever the vision
   * model returns is only pre-filled: the student confirms before it counts.
   */
  const readSlip = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setReading(true);
    try {
      const uploaded = await documentApi.upload(file, 'resultSlip');
      const id = uploaded.document?.id || uploaded.document?._id || uploaded.document?.id;
      if (!id) throw new Error('The slip uploaded but no document id came back. Please try again.');
      const read = await resultsApi.readSlip(id);
      if (!read.read) {
        notice.error(read.message || 'Entrance could not read that slip. Enter your grades by hand.');
        return;
      }
      setDraft({
        eng: read.result?.eng || 0,
        math: read.result?.math || 0,
        sci: read.result?.sci || 0,
        soc: read.result?.soc || 0,
        el: read.result?.el || form.el
      });
      notice.success(read.message || 'Slip read. Check the grades before saving.');
    } catch (error) {
      notice.fromError(error);
    } finally {
      setReading(false);
    }
  };

  const visible = programmes
    .filter((p) => filter === 'All' || p.statusLabel === filter)
    .filter((p) => school === 'All' || p.school.id === school);

  const schoolOptions = programmes.reduce((acc, p) => {
    if (!acc.some((s) => s.id === p.school.id)) acc.push(p.school);
    return acc;
  }, []);

  return (
    <>
      <h2>Entrance Match</h2>
      <Muted>
        Add your results. Entrance checks them against each training college's entrance rules and cut-offs.
      </Muted>

      <Grid2>
        <Card>
          <h3 style={{ fontSize: 18, marginBottom: 8 }}>Your results</h3>

          {slipNotice ? (
            <div className="note" style={{ marginBottom: 10 }}>
              <b>Slip read</b>
              <div className="mu" style={{ marginTop: 4 }}>
                {slipSeed?.message || 'Entrance filled in the grades it read.'} Check every grade
                below, then save.
              </div>
            </div>
          ) : null}

          <div className="note">
            <b>Upload your result slip</b>
            <div className="mu" style={{ margin: '4px 0 10px' }}>
              A photo, screenshot or PDF of your WASSCE slip or results checker. Entrance reads the
              grades and you confirm them before anything is saved.
            </div>
            <div className="row">
              <label className="btn sm" style={{ margin: 0, cursor: 'pointer', display: 'inline-block' }}>
                {reading ? 'Reading...' : 'Upload slip'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  onChange={readSlip}
                  disabled={reading}
                />
              </label>
              {current ? (
                <Button variant="s" size="sm" onClick={clear}>Clear results</Button>
              ) : null}
            </div>
          </div>

          <p className="hd">Core subjects</p>
          <Grid2>
            {[
              ['eng', 'English Language'],
              ['math', 'Core Mathematics'],
              ['sci', 'Integrated Science'],
              ['soc', 'Social Studies']
            ].map(([key, label]) => (
              <label key={key}>
                {label}
                <select aria-label={label} value={form[key] || ''} onChange={setCore(key)}>
                  <option value="">Grade</option>
                  {grades.map((g) => (
                    <option key={g} value={gradeValue(g)}>{g}</option>
                  ))}
                </select>
              </label>
            ))}
          </Grid2>

          <p className="hd">Electives (best three count)</p>
          {form.el.map((elective, i) => (
            <label key={i}>
              Elective {i + 1}
              <div className="row" style={{ flexWrap: 'nowrap' }}>
                <select
                  aria-label={`Elective ${i + 1} subject`}
                  value={elective.c || ''}
                  onChange={(e) => setElective(i, { c: e.target.value })}
                >
                  <option value="">Subject</option>
                  {Object.entries(subjects).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
                <select
                  aria-label={`Elective ${i + 1} grade`}
                  style={{ maxWidth: 96 }}
                  value={elective.g || ''}
                  onChange={(e) => setElective(i, { g: Number(e.target.value) || 0 })}
                >
                  <option value="">Grade</option>
                  {grades.map((g) => (
                    <option key={g} value={gradeValue(g)}>{g}</option>
                  ))}
                </select>
              </div>
            </label>
          ))}

          <div style={{ marginTop: 14 }}>
            <Button busy={saving} onClick={save}>Save results</Button>
          </div>
        </Card>

        <div>
          <Card style={{ textAlign: 'center' }}>
            {aggregate != null ? (
              <>
                <Muted>Your aggregate</Muted>
                <AggregateRing aggregate={aggregate} />
                <Tag tone={aggregate <= 18 ? 'ok' : 'mid'}>{results.data?.band}</Tag>
                <Muted>
                  Qualified for {counts.Qualified || 0} programmes. Fee-paying route for{' '}
                  {counts['Fee-paying'] || 0}. Reach for {counts.Reach || 0}.
                </Muted>
                <div style={{ marginTop: 10 }}>
                  <Button onClick={() => navigate('/advisor')}>Get advice</Button>
                </div>
              </>
            ) : (
              <>
                <Muted>Your aggregate</Muted>
                <div className="big" style={{ color: 'var(--mu)' }}>--</div>
                <Muted>
                  Add English, Mathematics, Science or Social Studies, and three electives.
                </Muted>
              </>
            )}
          </Card>
        </div>
      </Grid2>

      {match.loading ? <Spinner label="Checking every programme" /> : null}

      {aggregate != null && !match.loading ? (
        <>
          <div className="row" style={{ margin: '6px 0 10px' }}>
            <Chips
              label="Eligibility"
              options={['All', 'Qualified', 'Fee-paying', 'Reach', 'Long shot', 'Not eligible']}
              value={filter}
              onChange={(v) => {
                setFilter(v);
                setLimit(20);
              }}
            />
            <select
              aria-label="Filter by college"
              style={{ width: 'auto', margin: 0 }}
              value={school}
              onChange={(e) => {
                setSchool(e.target.value);
                setLimit(20);
              }}
            >
              <option value="All">All colleges</option>
              {schoolOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.shortName}</option>
              ))}
            </select>
          </div>

          <Card>
            {visible.length === 0 ? (
              <Muted>No programmes match this filter.</Muted>
            ) : (
              visible.slice(0, limit).map((p) => (
                <div className="prog" key={p.id} style={{ alignItems: 'flex-start' }}>
                  <i className="ub">{p.school.shortName}</i>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{p.name}</b>
                    <div className="mu">
                      {p.school.name} · cut-off {p.cutOff ?? 'not set'}
                      {p.cutOffEstimated ? ' (est.)' : ''}
                      {p.cutOffFeePaying ? ` · fee-paying ${p.cutOffFeePaying}` : ''}
                    </div>
                    <div className="mu">{p.reason}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Tag tone={statusTone[p.status]}>{p.statusLabel}</Tag>
                    <div style={{ marginTop: 8 }}>
                      <Button
                        size="sm"
                        variant="s"
                        onClick={() => navigate(`/schools/${p.school.slug}`)}
                      >
                        School
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
            {visible.length > limit ? (
              <Button variant="s" size="sm" onClick={() => setLimit((l) => l + 20)}>
                Show more ({visible.length - limit} remaining)
              </Button>
            ) : null}
          </Card>

          <div className="note">
            Cut-offs marked as estimates come from public sources and change every year. Confirm with
            the college before you pay.
          </div>
        </>
      ) : null}
    </>
  );
}
